import { Group, Stack, Text } from '@mantine/core';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import { STATUS_TONE_COLORS } from '../primitives/statusTone.utils';
import type { BatchListItemView } from './batchList.types';
import { BATCH_COUNTS_KEY, BATCH_PROGRESS_KEY, UNTITLED_BATCH_KEY } from './batchList.utils';

interface Props {
	item: BatchListItemView;
}

/** A batch as the lists show it: title, state, counts and start date, and its review's progress. */
export const BatchSummary = ({ item }: Props) => {
	const tCapManage = useTranslationCapManage();
	const { batch } = item;

	return (
		<Stack component="span" gap={2} align="flex-start" miw={0} w="100%">
			<Group
				component="span"
				align="center"
				justify="space-between"
				gap="xs"
				wrap="nowrap"
				w="100%"
				miw={0}
			>
				<Text
					component="span"
					size="md"
					fw={600}
					miw={0}
					style={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{batch.title ?? tCapManage(UNTITLED_BATCH_KEY)}
				</Text>
				<StatusBadge
					size="xs"
					{...STATUS_TONE_COLORS[item.stateTone]}
					text={tCapManage(item.stateLabelKey)}
				/>
			</Group>
			<Text span c="dark.0" fz="sm" lh="16px">
				{tCapManage(BATCH_COUNTS_KEY, {
					documents: batch.documentCount,
					rows: batch.transactionCount,
					date: item.createdOn,
				})}
			</Text>
			{item.progress && (
				<Text
					span
					c="dark.0"
					fz="sm"
					lh="16px"
					data-testid={`${TEST_ELEMENT_IDS.DEALSTACK_AI_BATCH_PROGRESS}_${batch.id}`}
				>
					{tCapManage(BATCH_PROGRESS_KEY, { ...item.progress })}
				</Text>
			)}
		</Stack>
	);
};
